import { recommendFullSet, recommendSize } from './alvorn-size-recommender.js';

function findProductScope(root) {
  const id = root?.dataset?.productId;
  return root?.parentElement?.closest?.(`[data-product-id="${id}"]`)
    || root?.closest?.('product-information')
    || root?.closest?.('.product-information')
    || root?.parentElement
    || document;
}

function selectSize(root, size, hint) {
  if (!size) return false;
  const scope = findProductScope(root);
  const fieldsets = [...scope.querySelectorAll('variant-picker fieldset')];
  const matchingFieldsets = fieldsets.filter((fieldset) => {
    const legend = fieldset.querySelector('legend')?.textContent || '';
    return hint ? hint.test(legend) : /beden|size/i.test(legend);
  });
  for (const fieldset of matchingFieldsets) {
    const input = [...fieldset.querySelectorAll('input[type="radio"]')].find((candidate) => (
      candidate.value.trim().toLocaleUpperCase('tr-TR') === String(size).trim().toLocaleUpperCase('tr-TR')
      && candidate.getAttribute('aria-disabled') !== 'true'
      && !candidate.disabled
    ));
    if (!input) continue;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  return false;
}

export function applyRecommendedSizes(root, result, category) {
  if (result?.status !== 'recommended') return false;
  if (category === 'full-set') {
    const topApplied = selectSize(root, result.top?.size, /üst|top/i);
    const bottomApplied = selectSize(root, result.bottom?.size, /alt|bottom/i);
    return topApplied && bottomApplied;
  }
  return selectSize(root, result.size, null);
}

function measurementValues(form, category) {
  const values = Object.fromEntries([...form.querySelectorAll('input[name]')].map((input) => [input.name, Number(input.value)]));
  if (category === 'full-set') {
    return {
      top: { chest: values.topChest, shoulder: values.topShoulder, torsoLength: values.topTorsoLength },
      bottom: { waist: values.bottomWaist, hip: values.bottomHip, thigh: values.bottomThigh, inseam: values.bottomInseam },
    };
  }
  return values;
}

function resultMessage(result, category) {
  if (result?.status === 'recommended') {
    if (category === 'full-set') return `Önerilen üst beden: ${result.top.size}. Önerilen alt beden: ${result.bottom.size}.`;
    return `Önerilen beden: ${result.size}. ${result.rationale.join('. ')}.`;
  }
  if (result?.status === 'needs-input') return `Eksik ölçüler: ${result.missing.join(', ')}.`;
  if (result?.status === 'unavailable') return 'Bu ürün için doğrulanmış beden verisi henüz bulunmuyor.';
  return result?.rationale?.join(' ') || 'Tek bir beden güvenle doğrulanamadı. Beden rehberini kullanın.';
}

export class AlvornFindYourSize extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialised === 'true') return;
    this.dataset.initialised = 'true';
    this.opener = this.querySelector('[data-alvorn-find-size-open]');
    this.dialog = this.querySelector('[data-alvorn-find-size-dialog]');
    this.form = this.querySelector('[data-alvorn-find-size-form]');
    this.output = this.querySelector('[data-alvorn-find-size-result]');
    try {
      this.profile = JSON.parse(this.querySelector('[data-alvorn-size-profile]')?.textContent || 'null');
    } catch {
      this.profile = null;
    }
    if (!this.opener || !this.dialog || !this.form || !this.output || !this.profile) {
      if (this.opener) this.opener.hidden = true;
      return;
    }

    this.onOpen = () => this.open();
    this.onCloseClick = () => this.close();
    this.onDialogClick = (event) => { if (event.target === this.dialog) this.close(); };
    this.onNativeClose = () => this.opener.focus();
    this.onSubmit = (event) => this.submit(event);
    this.opener.addEventListener('click', this.onOpen);
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('close', this.onNativeClose);
    this.form.addEventListener('submit', this.onSubmit);
    for (const button of this.querySelectorAll('[data-alvorn-find-size-close]')) button.addEventListener('click', this.onCloseClick);
  }

  disconnectedCallback() {
    if (!this.opener || !this.dialog || !this.form) return;
    this.opener.removeEventListener('click', this.onOpen);
    this.dialog.removeEventListener('click', this.onDialogClick);
    this.dialog.removeEventListener('close', this.onNativeClose);
    this.form.removeEventListener('submit', this.onSubmit);
    for (const button of this.querySelectorAll('[data-alvorn-find-size-close]')) button.removeEventListener('click', this.onCloseClick);
    delete this.dataset.initialised;
  }

  open() {
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    this.form.querySelector('input')?.focus();
  }

  close() {
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else {
      this.dialog.removeAttribute('open');
      this.opener.focus();
    }
  }

  submit(event) {
    event.preventDefault();
    const category = this.dataset.category;
    const measurements = measurementValues(this.form, category);
    const result = category === 'full-set'
      ? recommendFullSet(this.profile.top, this.profile.bottom, measurements)
      : recommendSize(this.profile, measurements);
    this.output.textContent = resultMessage(result, category);
    if (applyRecommendedSizes(this, result, category)) this.output.tabIndex = -1;
    this.output.focus();
  }
}

export function initialiseFindYourSize(root = document) {
  if (globalThis.customElements && !customElements.get('alvorn-find-your-size')) customElements.define('alvorn-find-your-size', AlvornFindYourSize);
  for (const element of root.querySelectorAll?.('alvorn-find-your-size') || []) element.connectedCallback?.();
}

if (typeof document !== 'undefined') {
  initialiseFindYourSize(document);
  document.addEventListener('shopify:section:load', (event) => initialiseFindYourSize(event.target));
}
